import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	BuildingOfficeIcon,
	UserIcon,
	EnvelopeIcon,
	PhoneIcon,
	MapPinIcon,
	GlobeAltIcon,
	BriefcaseIcon,
} from '@heroicons/react/24/outline';

import { getContact } from '../api/contacts';
import { classNames, getUserFullName } from '../components/helpers/functions';
import { StackableModal, StatusBadge } from '../components/nextgen';
import { AvatarGenerated } from '../components/Avatar';
import Button from '../components/Button';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import useAsync from '../hooks/useAsync';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'viewContact';

const VENDOR_TYPES = {
	manufacturer: 'Manufacturer',
	service_vendor: 'Service Vendor',
	distributor: 'Distributor',
	other: 'Other',
};

const ViewContactModal = () => {
	const navigate = useNavigate();
	const { open, setClose } = useModal(MODAL_NAME);

	const {
		value: contact,
		execute: refreshContact,
		status,
	} = useAsync(getContact, { immediate: false });

	useEffect(() => {
		if (open?.id) {
			refreshContact({ id: open?.id });
		}
	}, [open?.id, refreshContact]);

	const handleClose = () => {
		setClose();
		if (typeof open?.onClose === 'function') {
			open.onClose();
		}
	};

	// Get display name
	const getDisplayName = () => {
		if (!contact) return '';
		return contact.type === 'company'
			? contact.companyName || 'Unnamed Company'
			: getUserFullName(contact) || 'Unnamed Contact';
	};

	// Get vendor type label
	const getVendorType = () => {
		if (!contact) return '';
		if (contact.vendorType && VENDOR_TYPES[contact.vendorType]) {
			return VENDOR_TYPES[contact.vendorType];
		}
		return contact.type === 'company' ? 'Company' : 'Individual';
	};

	return (
		<StackableModal open={!!open} onClose={handleClose} size="md">
			{status === 'pending' && !contact ? (
				<LoadingSkeleton />
			) : !contact ? (
				<div className="text-center py-12">
					<p className="text-sm text-gray-500">Contact not found</p>
				</div>
			) : (
				<div className="space-y-6">
					{/* Header with Avatar and Basic Info */}
					<div className="flex items-start gap-4 pb-4 border-b border-gray-200">
						<div className="flex-shrink-0">
							{contact.type === 'person' ? (
								<AvatarGenerated size="sm" id={getUserFullName(contact)} />
							) : (
								<AvatarGenerated size="sm" id={contact?.companyName} />
							)}
						</div>
						<div className="flex-1 min-w-0">
							<div className="flex items-start justify-between">
								<div>
									<h2 className="text-2xl font-semibold text-gray-900">
										{getDisplayName()}
									</h2>
									<div className="flex items-center gap-3">
										<span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
											{' '}
											{getVendorType()}
										</span>
									</div>
								</div>
							</div>

							{/* Show title if person belongs to company */}
							{contact.type === 'person' && contact.title && (
								<p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
									<BriefcaseIcon className="w-4 h-4 text-gray-400" />
									{contact.title}
									{contact.company?.companyName && (
										<span className="text-gray-400">
											at {contact.company.companyName}
										</span>
									)}
								</p>
							)}
						</div>
					</div>

					{/* Contact Information */}
					<div>
						<dl className="grid grid-cols-1 gap-y-4">
							{contact.type === 'person' && (
								<div className="flex items-start gap-3">
									<BriefcaseIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
									<div className="min-w-0 flex-1">
										<dt className="text-xs font-medium text-gray-500">Title</dt>
										<dd className="mt-1 text-sm text-gray-900 break-words">
											{contact.title ? (
												contact.title
											) : (
												<span className="text-gray-400">—</span>
											)}
										</dd>
									</div>
								</div>
							)}

							<div className="flex items-start gap-3">
								<EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
								<div className="min-w-0 flex-1">
									<dt className="text-xs font-medium text-gray-500">Email</dt>
									<dd className="mt-1 text-sm text-gray-900 break-words">
										{contact.email ? (
											<a
												href={`mailto:${contact.email}`}
												className="text-indigo-600 hover:text-indigo-700"
											>
												{contact.email}
											</a>
										) : (
											<span className="text-gray-400">—</span>
										)}
									</dd>
								</div>
							</div>

							<div className="flex items-start gap-3">
								<PhoneIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
								<div className="min-w-0 flex-1">
									<dt className="text-xs font-medium text-gray-500">Phone</dt>
									<dd className="mt-1 text-sm text-gray-900">
										{contact.phoneNumber ? (
											<a
												href={`tel:${contact.phoneNumber}`}
												className="text-indigo-600 hover:text-indigo-700"
											>
												{contact.phoneNumber}
											</a>
										) : (
											<span className="text-gray-400">—</span>
										)}
									</dd>
								</div>
							</div>
						</dl>
					</div>

					{/* Individuals (for companies) */}
					{contact.type === 'company' &&
						contact.contacts &&
						contact.contacts.length > 0 && (
							<div>
								<h3 className="text-sm font-semibold text-gray-900 mb-3">
									Employees ({contact.contacts.length})
								</h3>
								<div className="bg-gray-50 rounded-lg border border-gray-200">
									<ul className="divide-y divide-gray-200">
										{contact.contacts.slice(0, 5).map((person) => (
											<li
												key={person.id}
												className="px-4 py-3 hover:bg-gray-100 transition-colors cursor-pointer"
												onClick={() => {
													handleClose();
													navigate(`/manage/directory/${person.id}`);
												}}
											>
												<div className="flex items-center gap-3">
													<AvatarGenerated
														size="xs"
														id={getUserFullName(person)}
													/>
													<div className="min-w-0 flex-1">
														<p className="text-sm font-medium text-gray-900">
															{getUserFullName(person)}
														</p>
														{person.title && (
															<p className="text-xs text-gray-500">
																{person.title}
															</p>
														)}
													</div>
													<div className="flex items-center gap-2 text-xs text-gray-500">
														{person.email && (
															<a
																href={`mailto:${person.email}`}
																className="text-indigo-600 hover:text-indigo-700"
																onClick={(e) => e.stopPropagation()}
															>
																<EnvelopeIcon className="w-4 h-4" />
															</a>
														)}
														{person.phone && (
															<a
																href={`tel:${person.phone}`}
																className="text-indigo-600 hover:text-indigo-700"
																onClick={(e) => e.stopPropagation()}
															>
																<PhoneIcon className="w-4 h-4" />
															</a>
														)}
													</div>
												</div>
											</li>
										))}
									</ul>
									{contact.contacts.length > 5 && (
										<div className="px-4 py-2 text-center border-t border-gray-200">
											<button
												onClick={() => {
													handleClose();
													navigate(`/manage/directory/${contact.id}`);
												}}
												className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
											>
												View all {contact.contacts.length} employees
											</button>
										</div>
									)}
								</div>
							</div>
						)}

					{/* Stats */}
					<div
						className={classNames(
							'grid gap-4 pt-4 border-t border-gray-200',
							contact.type === 'company' ? 'grid-cols-3' : 'grid-cols-2',
						)}
					>
						<div className="text-center">
							<dt className="text-xs font-medium text-gray-500">Assets</dt>
							<dd className="mt-1 text-2xl font-semibold text-gray-900">
								{contact._count?.assets || contact.assets?.length || 0}
							</dd>
						</div>
						<div className="text-center">
							<dt className="text-xs font-medium text-gray-500">Contracts</dt>
							<dd className="mt-1 text-2xl font-semibold text-gray-900">
								{contact._count?.contracts || 0}
							</dd>
						</div>
						{contact.type === 'company' && (
							<div className="text-center">
								<dt className="text-xs font-medium text-gray-500">Employees</dt>
								<dd className="mt-1 text-2xl font-semibold text-gray-900">
									{contact._count?.contacts || contact.contacts?.length || 0}
								</dd>
							</div>
						)}
					</div>

					{/* Footer Actions */}
					{!open?.disableFullProfile && (
						<div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
							<Button type="secondary" onClick={handleClose}>
								Close
							</Button>
							<Button
								onClick={() => {
									handleClose();
									if (typeof open?.onNavigate === 'function') {
										open.onNavigate();
									}
									navigate(`/manage/directory/${open?.id}`);
								}}
							>
								View Full Profile
							</Button>
						</div>
					)}
				</div>
			)}
		</StackableModal>
	);
};

export default ViewContactModal;
